import { ChangeDetectionStrategy, Component, forwardRef, input } from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { FieldBase } from '../shared/field-base';
import { FieldErrorComponent } from '../shared/field-error.component';

export type TextFieldType = 'text' | 'email' | 'tel' | 'password' | 'number';

@Component({
  selector: 'ds-text-field',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FieldErrorComponent],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TextFieldComponent),
      multi: true,
    },
  ],
  template: `
    <div class="ds-field">
      <label class="ds-field__label" [for]="fieldId">
        {{ label() }}
        @if (required()) {
          <span class="ds-field__required" aria-hidden="true">*</span>
        }
      </label>
      @if (helperText()) {
        <p class="ds-field__helper" [id]="fieldId + '-helper'">{{ helperText() }}</p>
      }
      <input
        class="ds-input"
        [id]="fieldId"
        [type]="type()"
        [value]="value() ?? ''"
        [disabled]="disabled()"
        [required]="required()"
        [placeholder]="placeholder() ?? ''"
        [attr.autocomplete]="autocomplete() ?? null"
        [attr.inputmode]="inputMode() ?? null"
        [attr.maxlength]="maxLength() ?? null"
        [attr.aria-invalid]="errorText() ? 'true' : null"
        [attr.aria-describedby]="describedBy()"
        (input)="handleInput($event)"
        (blur)="onTouched()"
      />
      @if (errorText()) {
        <ds-field-error [errorId]="fieldId + '-error'">{{ errorText() }}</ds-field-error>
      }
    </div>
  `,
})
export class TextFieldComponent extends FieldBase<string> {
  type = input<TextFieldType>('text');
  placeholder = input<string | undefined>(undefined);
  autocomplete = input<string | undefined>(undefined);
  inputMode = input<string | undefined>(undefined);
  maxLength = input<number | undefined>(undefined);

  protected handleInput(event: Event): void {
    const next = (event.target as HTMLInputElement).value;
    this.value.set(next);
    this.onChange(next);
  }
}
